import { defineStore } from 'pinia';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  IReleaseDTO,
  IReleaseTypeDTO,
  ISubmissionCastCrewDTO,
  ReleaseDTO,
  ReleaseTypeDTO,
  SubmissionCastCrewDTO,
} from '@/dtos';
import {
  getReleaseForTalenAPI,
  getReleaseTypesAPI,
  getSubmissionReleasesAPI,
  removeReleaseAPI,
  saveReleaseAPI,
  sendEmailToTalentAPI,
  updateReleaseDonesAPI,
} from '@/services';
import { Ref } from 'vue';

export const useReleaseStore = defineStore('release', () => {
  const queryClient = useQueryClient();

  const getReleaseTypes = () => {
    return useQuery({
      queryKey: ['releaseTypes'],
      queryFn: async (): Promise<{ data: IReleaseTypeDTO[]; count: number }> => {
        const ret = await getReleaseTypesAPI();
        const types = ret.data.map((type) => new ReleaseTypeDTO(type));

        return { data: types, count: ret.count };
      },
    });
  };

  const getReleasesForSubmission = (submissionId: string) => {
    return useQuery({
      queryKey: ['releases', submissionId],
      queryFn: async (): Promise<{ data: IReleaseDTO[]; count: number }> => {
        const ret = await getSubmissionReleasesAPI(submissionId);
        const releases = ret.data.map((type) => new ReleaseDTO(type));

        return { data: releases, count: ret.count };
      },
      enabled: !!submissionId,
    });
  };

  const getTalentRelease = (email: string, token: string, errors: Ref<boolean>) => {
    return useQuery({
      queryKey: ['releases', email, token],
      queryFn: async (): Promise<ISubmissionCastCrewDTO> => {
        try {
          const ret = await getReleaseForTalenAPI(email, token);

          const release = new SubmissionCastCrewDTO(ret);

          return release;
        } catch (error) {
          console.error('Error fetching talent release:', error);
          errors.value = true;
          throw error; // Re-throw the error to handle it in the component
        }
      },
      enabled: errors.value == false,
      retry: false,
    });
  };

  const save = useMutation({
    mutationFn: (release: IReleaseDTO) => {
      return saveReleaseAPI(release);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
    onError: (err: string) => {
      console.error('Error saveing release:', err);
    },
  });

  const remove = useMutation({
    mutationFn: (release: IReleaseDTO) => {
      return removeReleaseAPI(release);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
    onError: (err: string) => {
      console.error('Error saveing release:', err);
    },
  });

  const markDone = useMutation({
    mutationFn: (submissionId: string) => {
      return updateReleaseDonesAPI(submissionId, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-registration'] });
    },
    onError: (err: string) => {
      console.error('Error saving release:', err);
    },
  });
  const markNotDone = useMutation({
    mutationFn: (submissionId: string) => {
      return updateReleaseDonesAPI(submissionId, false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-registration'] });
    },
    onError: (err: string) => {
      console.error('Error saving release:', err);
    },
  });

  const sendEmail = useMutation({
    mutationFn: ({
      submissionId,
      castCrew,
    }: {
      submissionId: string;
      castCrew: ISubmissionCastCrewDTO;
    }) => {
      return sendEmailToTalentAPI(submissionId, castCrew);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-registration'] });
    },
    onError: (err: string) => {
      console.error('Error saving release:', err);
    },
  });

  const invalidate = async () => {
    queryClient.invalidateQueries({ queryKey: ['releases'] });
  };

  return {
    getReleaseTypes,
    getReleasesForSubmission,
    save,
    remove,
    markDone,
    markNotDone,
    invalidate,
    sendEmail,
    getTalentRelease,
  };
});
